import * as Phaser from 'phaser';
import { difficultyColorForLevel } from '../difficultyPalette';
import type { Board } from '../model/Board';
import { directionVectors } from '../model/cells';
import { polylineLength } from '../model/Polyline';
import type { ArrowDefinition, Cell, Direction } from '../model/types';
import {
  arrowHeadLength,
  arrowHeadOvershoot,
  arrowHeadWidth,
  boardLatticeAlpha,
  boardLatticeColor,
  boardLatticeGradeMix,
  boardLatticeTexelWidth,
  cellSize,
  cornerRadius,
  cornerSegments,
  exitFadeSpan,
  exitFlashColor,
  exitFlashDuration,
  exitFlightColor,
  exitIntensityExponent,
  exitIntensityFloor,
  exitSpeedTint,
  hoverColor,
  lineColor,
  lineWidth,
  mixColor,
  octagonAt,
  pingAlpha,
  pingDepth,
  pingDuration,
  pingEndRadius,
  pingLineWidth,
  pingStartRadius,
  tailDotRadius,
  wakeFadeDuration,
  wakePasses,
} from './arrowStyle';
import { boardTextureSize, resolveBoardLayerScale, shouldRebakeBoardLayers } from './boardTexture';
import { buildLaneGrid, type LaneGrid } from './laneGrid';
import { cumulativeLengths, writePolylineWindow } from './pathWindow';
import { roundPolyline } from './roundedPath';

const staticBoardPadding = 3;

export interface ArrowView {
  readonly definition: ArrowDefinition;
  readonly container: Phaser.GameObjects.Container;
  readonly graphics: Phaser.GameObjects.Graphics;
  readonly hitRoute: readonly Cell[];
  /** Corner-rounded, flattened route built once so slicing never re-rounds it. */
  readonly displayRoute: readonly Cell[];
  readonly cumulativeDisplayRoute: readonly number[];
  readonly routeScratch: Array<{ x: number; y: number }>;
  /** Visible window length measured along `displayRoute`. */
  readonly originalLength: number;
  readonly successProgress: number;
  busy: boolean;
  progress: number;
  color: number;
}

export class ArrowRenderer {
  private readonly arrowViews = new Map<string, ArrowView>();
  private gridLayer: Phaser.GameObjects.RenderTexture | null = null;
  private staticArrowLayer: Phaser.GameObjects.RenderTexture | null = null;
  private staticBoardScratch: Phaser.GameObjects.Graphics | null = null;
  private staticArrowLayerDirty = false;
  private restoreListenerInstalled = false;
  private laneGrid: LaneGrid = { vertical: [], horizontal: [] };
  private layerWidth = 0;
  private layerHeight = 0;
  private layerScale = 0;
  /**
   * ワールド 1 単位あたりの実テクセル数。`layerScale` そのものではなく、
   * RenderTexture が実際に確保した寸法から逆算する（Phaser は奇数寸法を
   * 偶数へ切り上げるので、要求値のままだと 1 テクセルぶんずれる）。
   */
  private bakeScaleX = 1;
  private bakeScaleY = 1;
  private readonly restoreWebGlHandler = (): void => {
    this.bakeGridLayer();
    this.markStaticArrowLayerDirty();
    this.flushStaticArrowLayerBake();
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly board: Board,
    private readonly currentLevelNumber: number,
    private readonly resolveColor: (view: ArrowView) => number,
    private readonly getHoveredArrowId: () => string | null,
    private readonly getPrefersReducedMotion: () => boolean,
  ) {}

  createStaticBoardLayers(cameraZoom: number): void {
    const { width, height, arrows } = this.board.level;
    this.laneGrid = buildLaneGrid(arrows, width, height);
    this.layerWidth = width * cellSize + staticBoardPadding * 2;
    this.layerHeight = height * cellSize + staticBoardPadding * 2;
    this.layerScale = this.resolveLayerScale(cameraZoom);
    this.gridLayer = this.createLayer(-20).setAlpha(boardLatticeAlpha);
    this.staticArrowLayer = this.createLayer(10);
    this.syncBakeScale();
    this.staticBoardScratch = this.scene.make.graphics({}, false);
  }

  private createLayer(depth: number): Phaser.GameObjects.RenderTexture {
    return this.scene.add
      .renderTexture(
        -staticBoardPadding,
        -staticBoardPadding,
        boardTextureSize(this.layerWidth, this.layerScale),
        boardTextureSize(this.layerHeight, this.layerScale),
      )
      .setOrigin(0)
      .setDisplaySize(this.layerWidth, this.layerHeight)
      .setDepth(depth);
  }

  private resolveLayerScale(cameraZoom: number): number {
    const renderer = this.scene.game.renderer;
    const maxTextureSize = 'getMaxTextureSize' in renderer
      ? renderer.getMaxTextureSize()
      : Number.POSITIVE_INFINITY;
    return resolveBoardLayerScale({
      cameraZoom,
      layerWidth: this.layerWidth,
      layerHeight: this.layerHeight,
      maxTextureSize,
    });
  }

  private syncBakeScale(): void {
    const frame = this.gridLayer?.frame;
    this.bakeScaleX = frame === undefined ? this.layerScale : frame.realWidth / this.layerWidth;
    this.bakeScaleY = frame === undefined ? this.layerScale : frame.realHeight / this.layerHeight;
  }

  /**
   * カメラ倍率に見合う解像度へ焼き込み層を作り直す。ピンチ操作が落ち着いてから
   * 呼ばれる想定で、必要が無ければ何もしない。
   */
  syncLayerScale(cameraZoom: number): void {
    const layers = [this.gridLayer, this.staticArrowLayer];
    if (layers.some((layer) => layer === null)) {
      return;
    }
    const desired = this.resolveLayerScale(cameraZoom);
    if (!shouldRebakeBoardLayers(this.layerScale, desired)) {
      return;
    }

    this.layerScale = desired;
    for (const layer of layers) {
      layer
        ?.resize(
          boardTextureSize(this.layerWidth, this.layerScale),
          boardTextureSize(this.layerHeight, this.layerScale),
        )
        .setOrigin(0)
        .setDisplaySize(this.layerWidth, this.layerHeight);
    }
    this.syncBakeScale();
    this.bakeGridLayer();
    this.markStaticArrowLayerDirty();
    this.flushStaticArrowLayerBake();
  }

  bakeGridLayer(): void {
    const layer = this.gridLayer;
    const scratch = this.staticBoardScratch;
    if (layer === null || scratch === null) {
      return;
    }

    scratch.clear();
    this.drawBoardGuide(scratch);
    this.bakeScratch(layer, scratch);
  }

  /**
   * scratch をテクセル座標へ拡大して焼き込む。Graphics 側のスケールで拡大する
   * ので、線幅・角の丸めも含めてそのまま解像度が上がる。
   */
  private bakeScratch(
    layer: Phaser.GameObjects.RenderTexture,
    scratch: Phaser.GameObjects.Graphics,
  ): void {
    scratch.setScale(this.bakeScaleX, this.bakeScaleY);
    layer
      .clear()
      .draw(scratch, staticBoardPadding * this.bakeScaleX, staticBoardPadding * this.bakeScaleY)
      .render();
  }

  private drawBoardGuide(graphics: Phaser.GameObjects.Graphics): void {
    const gradeColor = difficultyColorForLevel(this.currentLevelNumber);
    // 層全体を boardLatticeAlpha で薄めるので、線そのものは不透明で描く。
    graphics.fillStyle(mixColor(boardLatticeColor, gradeColor, boardLatticeGradeMix), 1);

    const width = boardLatticeTexelWidth / Math.max(this.bakeScaleX, 0.0001);
    const half = width / 2;
    // 両端を半幅だけ伸ばして、縦横の線が交わる角に欠けが出ないようにする。
    for (const segment of this.laneGrid.vertical) {
      graphics.fillRect(
        segment.line * cellSize - half,
        segment.from * cellSize - half,
        width,
        (segment.to - segment.from) * cellSize + width,
      );
    }
    for (const segment of this.laneGrid.horizontal) {
      graphics.fillRect(
        segment.from * cellSize - half,
        segment.line * cellSize - half,
        (segment.to - segment.from) * cellSize + width,
        width,
      );
    }
  }

  markStaticArrowLayerDirty(): void {
    this.staticArrowLayerDirty = true;
  }

  flushStaticArrowLayerBake(): void {
    if (!this.staticArrowLayerDirty) {
      return;
    }
    this.staticArrowLayerDirty = false;

    const layer = this.staticArrowLayer;
    const scratch = this.staticBoardScratch;
    if (layer === null || scratch === null) {
      return;
    }

    scratch.clear();
    const hoveredArrowId = this.getHoveredArrowId();
    this.arrowViews.forEach((view) => {
      const isLive = view.busy || view.definition.id === hoveredArrowId;
      view.graphics.setVisible(isLive);
      if (!isLive && view.container.active) {
        const color = this.resolveColor(view);
        view.color = color;
        this.drawArrowGeometry(view, scratch, color);
      }
    });

    this.bakeScratch(layer, scratch);
  }

  createArrowViews(): void {
    this.board.activeArrows.forEach((arrow) => {
      this.arrowViews.set(arrow.id, this.createArrowView(arrow));
    });
  }

  resetArrowViews(): void {
    this.arrowViews.clear();
  }

  getView(arrowId: string): ArrowView | undefined {
    return this.arrowViews.get(arrowId);
  }

  deleteView(arrowId: string): void {
    this.arrowViews.delete(arrowId);
  }

  get viewCount(): number {
    return this.arrowViews.size;
  }

  cellToWorld(cell: Cell): Cell {
    return {
      x: cell.x * cellSize + cellSize / 2,
      y: cell.y * cellSize + cellSize / 2,
    };
  }

  getDistanceFromTipToOutside(tip: Cell, direction: Direction): number {
    const boardWidth = this.board.level.width * cellSize;
    const boardHeight = this.board.level.height * cellSize;
    switch (direction) {
      case 'up':
        return tip.y + cellSize;
      case 'right':
        return boardWidth - tip.x + cellSize;
      case 'down':
        return boardHeight - tip.y + cellSize;
      case 'left':
        return tip.x + cellSize;
    }
  }

  private createArrowView(definition: ArrowDefinition): ArrowView {
    const container = this.scene.add.container(0, 0).setDepth(10);
    const graphics = this.scene.add.graphics();
    const originalRoute = definition.cells.map((cell) => this.cellToWorld(cell));
    const tip = originalRoute.at(-1);
    if (tip === undefined) {
      throw new Error(`Arrow ${definition.id} has no tip.`);
    }
    const direction = directionVectors[definition.direction];
    const exitLength =
      polylineLength(originalRoute) + this.getDistanceFromTipToOutside(tip, definition.direction);
    const route = [
      ...originalRoute,
      {
        x: tip.x + direction.x * exitLength,
        y: tip.y + direction.y * exitLength,
      },
    ];
    // Round the whole route (board path plus the straight exit lane) exactly once.
    // Every frame afterwards only slices this polyline by arc length, so a corner
    // can never be re-rounded mid-animation.
    const displayRoute = roundPolyline(route, cornerRadius, cornerSegments);
    const cumulativeDisplayRoute = cumulativeLengths(displayRoute);
    const routeEnd = route.at(-1);
    const beforeRouteEnd = route.at(-2);
    if (routeEnd === undefined || beforeRouteEnd === undefined) {
      throw new Error(`Arrow ${definition.id} has no exit segment.`);
    }
    const exitSegmentLength = Math.hypot(
      routeEnd.x - beforeRouteEnd.x,
      routeEnd.y - beforeRouteEnd.y,
    );
    const originalLength = polylineLength(displayRoute) - exitSegmentLength;
    const successProgress = polylineLength(displayRoute) - originalLength;
    container.add(graphics);
    const view: ArrowView = {
      definition,
      container,
      graphics,
      hitRoute: originalRoute,
      displayRoute,
      cumulativeDisplayRoute,
      routeScratch: Array.from(
        { length: displayRoute.length + 2 },
        () => ({ x: 0, y: 0 }),
      ),
      originalLength,
      successProgress,
      busy: false,
      progress: 0,
      color: lineColor,
    };
    this.drawArrow(view, lineColor);
    return view;
  }

  drawArrow(view: ArrowView, color: number): void {
    const { graphics } = view;
    if (!graphics.active) {
      return;
    }

    view.color = color;
    graphics.clear();
    this.drawArrowGeometry(view, graphics, color);
  }

  private drawArrowGeometry(
    view: ArrowView,
    graphics: Phaser.GameObjects.Graphics,
    color: number,
  ): void {
    const { definition } = view;
    const visiblePointCount = this.strokeRouteWindow(
      view,
      graphics,
      view.progress,
      view.progress + view.originalLength,
      lineWidth,
      color,
      1,
    );
    const firstPoint = view.routeScratch[0];
    const tipPoint = view.routeScratch[visiblePointCount - 1];
    if (firstPoint === undefined || tipPoint === undefined) {
      return;
    }

    // Phaser's Graphics stroke has no round join/cap, so the joints are filled
    // in by hand to keep the rounded corners smooth at this line width.
    graphics.fillStyle(color, 1);
    for (let index = 1; index < visiblePointCount - 1; index += 1) {
      const joint = view.routeScratch[index];
      if (joint !== undefined) {
        graphics.fillPoints(octagonAt(joint, lineWidth / 2), true, true);
      }
    }
    graphics.fillPoints(octagonAt(firstPoint, tailDotRadius), true, true);

    const direction = directionVectors[definition.direction];
    const perpendicularX = -direction.y;
    const perpendicularY = direction.x;
    const arrowPointX = tipPoint.x + direction.x * arrowHeadOvershoot;
    const arrowPointY = tipPoint.y + direction.y * arrowHeadOvershoot;
    const baseCenterX = arrowPointX - direction.x * arrowHeadLength;
    const baseCenterY = arrowPointY - direction.y * arrowHeadLength;
    const halfWidth = arrowHeadWidth / 2;
    graphics.fillTriangle(
      arrowPointX,
      arrowPointY,
      baseCenterX + perpendicularX * halfWidth,
      baseCenterY + perpendicularY * halfWidth,
      baseCenterX - perpendicularX * halfWidth,
      baseCenterY - perpendicularY * halfWidth,
    );
  }

  /**
   * `displayRoute` を弧長 [start, end] でスライスして 1 本のストロークとして描き、
   * `view.routeScratch` に書いた点数を返す（本体・航跡の両方が共有する唯一の
   * ストローク経路）。呼び出し後も点は routeScratch に残るため、本体側は続けて
   * 関節や矢頭を描ける。新規アロケーションは行わない。
   *
   * 角の八角形埋めをここでやらないのは、半透明のパスで塗りとストロークが重なると
   * その一点だけ不透明度が倍になり、盤面上に等間隔の玉が並んで見えてしまうため
   * （航跡がこの経路を低アルファで使う）。角に残る欠けは幅に比例するので、
   * 本体幅以下の航跡では視認できない。
   */
  private strokeRouteWindow(
    view: ArrowView,
    graphics: Phaser.GameObjects.Graphics,
    start: number,
    end: number,
    width: number,
    color: number,
    alpha: number,
  ): number {
    const pointCount = writePolylineWindow(
      view.displayRoute,
      view.cumulativeDisplayRoute,
      start,
      end,
      view.routeScratch,
    );
    const firstPoint = view.routeScratch[0];
    if (pointCount < 2 || firstPoint === undefined) {
      return pointCount;
    }

    graphics.lineStyle(width, color, alpha);
    graphics.beginPath();
    graphics.moveTo(firstPoint.x, firstPoint.y);
    for (let index = 1; index < pointCount; index += 1) {
      const next = view.routeScratch[index];
      if (next !== undefined) {
        graphics.lineTo(next.x, next.y);
      }
    }
    graphics.strokePath();
    return pointCount;
  }

  /** 退出 tween の 1 フレーム分。航跡の長さと芯の明るさを速度に連動させる。 */
  drawExitFrame(
    view: ArrowView,
    wake: Phaser.GameObjects.Graphics,
    wakeSpan: number,
    flashStartedAt: number,
  ): void {
    const travelled = view.successProgress > 0
      ? Phaser.Math.Clamp(view.progress / view.successProgress, 0, 1)
      : 1;
    const intensity = exitIntensityFloor +
      (1 - exitIntensityFloor) * Math.pow(travelled, exitIntensityExponent);
    // 長さは速度そのもの、明るさは平方根で早めに立ち上げる（速度に比例させると
    // 実際に速い最後の数フレームまで航跡が見えないため）。
    const brightness = Math.sqrt(intensity);
    this.drawWake(view, wake, wakeSpan * intensity, brightness);

    // 尾端が盤外へ抜けきる最後の 1 マス分でだけ本体を溶かし、消滅のハードカットを隠す。
    // 航跡は別レイヤーなのでここでは消えず、飛び去ったあとの余韻として残る。
    if (view.container.active) {
      view.container.alpha = Phaser.Math.Clamp(
        (view.successProgress - view.progress) / exitFadeSpan,
        0,
        1,
      );
    }

    const core = mixColor(exitFlightColor, hoverColor, brightness * exitSpeedTint);
    const flashLeft = Phaser.Math.Clamp(
      1 - (this.scene.time.now - flashStartedAt) / exitFlashDuration,
      0,
      1,
    );
    this.drawArrow(
      view,
      flashLeft > 0 ? mixColor(core, exitFlashColor, flashLeft * flashLeft * flashLeft) : core,
    );
  }

  /** タップ地点から細いリングが 1 本広がって消える、レーダー走査のピング。 */
  spawnTapPing(origin: Cell): void {
    if (this.getPrefersReducedMotion()) {
      return;
    }

    const ping = this.scene.add.graphics().setDepth(pingDepth);
    const state = { spread: 0 };
    this.scene.tweens.add({
      targets: state,
      spread: 1,
      duration: pingDuration,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        if (!ping.active) {
          return;
        }
        // CSS ピクセル指定なので、devicePixelRatio ぶんの拡大（ScaleManager の
        // zoom = 1/dpr）を戻したうえで、ピンチズームにも毎フレーム追従する。
        const toWorld = 1 / (this.scene.scale.zoom * Math.max(this.scene.cameras.main.zoom, 0.0001));
        const fade = 1 - state.spread;
        const radius = pingStartRadius + (pingEndRadius - pingStartRadius) * state.spread;
        ping.clear();
        ping.lineStyle(pingLineWidth * toWorld, hoverColor, pingAlpha * fade * fade);
        ping.strokeCircle(origin.x, origin.y, radius * toWorld);
      },
      onComplete: () => ping.destroy(),
    });
  }

  private drawWake(
    view: ArrowView,
    wake: Phaser.GameObjects.Graphics,
    span: number,
    brightness: number,
  ): void {
    if (!wake.active) {
      return;
    }
    wake.clear();
    if (span < 1) {
      return;
    }

    for (const pass of wakePasses) {
      this.strokeRouteWindow(
        view,
        wake,
        view.progress - span * pass.spanRatio,
        view.progress,
        lineWidth * pass.widthRatio,
        pass.color,
        pass.alpha * brightness,
      );
    }
  }

  fadeOutWake(wake: Phaser.GameObjects.Graphics | null): void {
    if (wake === null || !wake.active) {
      return;
    }
    this.scene.tweens.add({
      targets: wake,
      alpha: 0,
      duration: wakeFadeDuration,
      ease: 'Sine.easeOut',
      onComplete: () => wake.destroy(),
    });
  }

  listenForWebGlRestore(): void {
    if (this.restoreListenerInstalled) {
      return;
    }
    this.scene.game.renderer.on(Phaser.Renderer.Events.RESTORE_WEBGL, this.restoreWebGlHandler);
    this.restoreListenerInstalled = true;
  }

  destroy(): void {
    this.gridLayer?.destroy();
    this.gridLayer = null;
    this.staticArrowLayer?.destroy();
    this.staticArrowLayer = null;
    this.staticBoardScratch?.destroy();
    this.staticBoardScratch = null;
    this.staticArrowLayerDirty = false;
    if (this.restoreListenerInstalled) {
      this.scene.game.renderer.off(Phaser.Renderer.Events.RESTORE_WEBGL, this.restoreWebGlHandler);
      this.restoreListenerInstalled = false;
    }
  }
}
